import React, { useMemo } from 'react';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import {
  Box,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger, Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import PINSAIAssistantLabel from '../../../../../../../PINSAIAssistantLabel';
import PINSAIIconButton from '../../../../../../../PINSAIIconButton';

const TemplateBlockQuestionsPopover = ({ blueprint, templateBlock, cypressId, requirementTypeId, requirementType }) => {
  const dispatch = useDispatch();
  const templateBlockQuestions = templateBlock?.requirementTemplateQuestions ?? [];

  const closeButtonCypressId = useMemo(() => {
    if (requirementType === 'insurance_policy_type') {
      return CypressIds.closeQuestionLinkPolicyTypePopover(requirementTypeId);
    }

    if (requirementType === 'document_type') {
      return CypressIds.closeQuestionLinkDocumentTypePopover(requirementTypeId);
    }

    return null;
  }, [requirementType]);

  return (
    <HStack h="40px">
      <Popover placement="bottom">
        <PopoverTrigger>
          <Box>
            <PINSAIIconButton
              aria-label="Delete Requirement Block"
              data-cy={cypressId}
            />
          </Box>
        </PopoverTrigger>
        <PopoverContent _hover={{ cursor: 'default' }} maxW="1400px" p={4}>
          <PopoverArrow />
          <PopoverCloseButton data-cy={closeButtonCypressId} />
          <PopoverBody>
            <PINSAIAssistantLabel mb={4} w="333px" />
            <Text
              color="greyScale.700"
              mb={6}
              textAlign="left"
              textStyle="bodyXxl600"
            >
              {`Linked Questions (${templateBlockQuestions.length})`}
            </Text>
            <Box
              bg={colors.greyScale['75']}
              marginTop="0 !important"
              maxHeight="400px"
              overflowY="auto"
              px={2}
              width="100%"
            >
              <Grid
                bg="inherit"
                color={colors.greyScale['800']}
                gap={6}
                gridTemplateColumns="repeat(10, 1fr);"
                h="44px"
                position="sticky"
                py={2}
                rowGap={6}
                textStyle="bodySm500"
                top="initial"
                width="100%"
                zIndex="initial"
              >
                <GridItem alignSelf="center" colSpan={4} justifySelf="left" ml={2}>Question Name</GridItem>
                <GridItem alignSelf="center" colSpan={5} justifySelf="left" mr={2}>Question Description</GridItem>
                <GridItem alignSelf="center" colSpan={1} justifySelf="left" mr={2} />
              </Grid>
            </Box>
            <Box
              maxH="300px"
              overflowY="auto"
              px={2}
            >
              {templateBlockQuestions.map(templateBlockQuestion => {
                const question = templateBlockQuestion?.question;

                let questionCypressId;

                if (requirementType === 'document_type') {
                  questionCypressId = CypressIds.recordQuestionDocLinkDeleteIcon(requirementTypeId, question.id);
                } else if (requirementType === 'insurance_policy_type') {
                  questionCypressId = CypressIds.recordQuestionPolicyLinkDeleteIcon(requirementTypeId, question.id);
                }

                return (
                  <Box
                    bg="white"
                    key={templateBlockQuestion?.id}
                    marginTop="0 !important"
                    maxHeight="400px"
                    overflowY="auto"
                    width="100%"
                  >
                    <Grid
                      bg="inherit"
                      gap={6}
                      gridTemplateColumns="repeat(10, 1fr);"
                      position="sticky"
                      py={2}
                      rowGap={6}
                      textStyle="bodySm"
                      top="initial"
                      width="100%"
                      zIndex="initial"
                    >
                      <GridItem
                        alignSelf="center"
                        colSpan={4}
                        data-cy={CypressIds.recordQuestionQuestionName(question.id)}
                        justifySelf="left"
                        ml={2}
                        mr={10}
                        textStyle="bodyMd500"
                      >
                        {question.name}
                      </GridItem>
                      <GridItem
                        alignSelf="center"
                        colSpan={5}
                        data-cy={CypressIds.recordQuestionQuestion(question.id)}
                        justifySelf="left"
                        mr={2}
                        textStyle="bodyMd"
                      >
                        {question.question}
                      </GridItem>
                      <GridItem alignSelf="center" colSpan={1} justifySelf="center" mr={2}>
                        <IconButton
                          aria-label="Delete Requirement Question Link Button"
                          data-cy={questionCypressId}
                          icon={<Icon as={BsTrash} color={colors.error.primary['600']} h="20px" w="20px" />}
                          variant="textGrey"
                          onClick={() => {
                            const { id: requirementTemplateBlockId } = templateBlock;

                            dispatch(blueprint.deleteQuestionRequirementBlock(templateBlockQuestion.id, requirementTemplateBlockId));
                          }}
                        />
                      </GridItem>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Divider orientation="vertical" />
    </HStack>
  );
};

TemplateBlockQuestionsPopover.propTypes = {
  blueprint: RequirementTemplateBlueprintType,
  templateBlock: PropTypes.object,
  requirementTypeId: PropTypes.number,
  requirementType: PropTypes.string
};

export default TemplateBlockQuestionsPopover;
